import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { socialLinks, WHATSAPP_LINK } from '../data/mock';
import { MapPin, Phone, MessageCircle, Instagram, Facebook, ArrowUp } from 'lucide-react';

export const Footer = () => {
  const { t } = useLanguage();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const navLinks = [
    { key: 'about', href: '#about' },
    { key: 'locations', href: '#locations' },
    { key: 'menu', href: '#menu' },
    { key: 'gallery', href: '#gallery' },
    { key: 'reviews', href: '#reviews' },
  ];

  return (
    <footer className="relative bg-[#0C4A6E] text-white">
      {/* Wave decoration */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#0D9488] via-[#2DD4BF] to-[#0D9488]" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-20 pb-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          {/* Brand */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-5">
              <img
                src="/assets/images/logo/logo.png"
                alt="Cristian's Baja Salada"
                className="h-16 w-auto brightness-[2] drop-shadow-[0_0_8px_rgba(255,255,255,0.3)]"
              />
            </div>
            <p className="text-white/60 text-sm leading-relaxed">
              {t('footer.description')}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-display font-bold text-sm tracking-wider uppercase text-white/40 mb-5">
              {t('footer.quickLinks')}
            </h4>
            <nav className="flex flex-col gap-2.5">
              {navLinks.map((link) => (
                <a
                  key={link.key}
                  href={link.href}
                  onClick={(e) => {
                    e.preventDefault();
                    document.querySelector(link.href)?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="text-white/60 text-sm hover:text-[#2DD4BF] transition-colors duration-300"
                >
                  {t(`nav.${link.key}`)}
                </a>
              ))}
            </nav>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-display font-bold text-sm tracking-wider uppercase text-white/40 mb-5">
              {t('footer.contact')}
            </h4>
            <div className="space-y-4">
              <a href="tel:+526243582926" className="flex items-center gap-3 text-white/60 text-sm hover:text-[#2DD4BF] transition-colors">
                <Phone className="w-4 h-4 flex-shrink-0" />
                +52 624 358 2926
              </a>
              <a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 text-white/60 text-sm hover:text-[#2DD4BF] transition-colors"
              >
                <MessageCircle className="w-4 h-4 flex-shrink-0" />
                WhatsApp
              </a>
              <div className="flex items-start gap-3 text-white/60 text-sm">
                <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>Cabo San Lucas & San José del Cabo, B.C.S., Mexico</span>
              </div>
            </div>
          </div>

          {/* Social */}
          <div>
            <h4 className="font-display font-bold text-sm tracking-wider uppercase text-white/40 mb-5">
              {t('footer.followUs')}
            </h4>
            <div className="flex gap-3">
              <a
                href={socialLinks.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center text-white/60 hover:bg-[#E1306C]/20 hover:text-[#E1306C] transition-all duration-300"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href={socialLinks.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center text-white/60 hover:bg-[#1877F2]/20 hover:text-[#1877F2] transition-all duration-300"
              >
                <Facebook className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>

        {/* Bottom */}
        <div className="pt-8 border-t border-white/10 flex flex-col items-center gap-3">
          <div className="w-full flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-white/40 text-sm">
              {t('footer.rights')}
            </p>
            <div className="flex items-center gap-4">
              <p className="text-white/30 text-xs">
                {t('footer.madeWith')}
              </p>
              <button
                onClick={scrollToTop}
                className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center text-white/40 hover:bg-[#0D9488] hover:text-white transition-all duration-300"
                aria-label="Back to top"
              >
                <ArrowUp className="w-4 h-4" />
              </button>
            </div>
          </div>
          <div className="text-center text-white/30 text-xs pb-1">
            <p>
              Proudly made by{' '}
              <a
                href="https://cabodigitalelite.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#2DD4BF]/70 hover:text-[#2DD4BF] transition-colors duration-300 underline underline-offset-2"
              >
                Cabo Digital Elite
              </a>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};
